/**
 * Request deduplication utilities to prevent race conditions
 */

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

class RequestDeduplicator {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private pendingRequests = new Map<string, PendingRequest<any>>();
  private readonly defaultTimeout = 30000; // 30 seconds

  /**
   * Deduplicates requests by key, ensuring only one request per key is active at a time
   */
  async deduplicate<T>(
    key: string,
    requestFn: () => Promise<T>,
    timeoutMs: number = this.defaultTimeout
  ): Promise<T> {
    // Check if there's already a pending request for this key
    const existing = this.pendingRequests.get(key);

    if (existing) {
      const age = Date.now() - existing.timestamp;

      // If the existing request is still fresh, return it
      if (age < timeoutMs) {
        return existing.promise;
      } else {
        // Remove stale request
        this.pendingRequests.delete(key);
      }
    }

    // Create new request
    const promise = requestFn().finally(() => {
      // Clean up after completion (success or failure)
      this.pendingRequests.delete(key);
    });

    // Store the pending request
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now(),
    });

    return promise;
  }

  /**
   * Cancels a pending request by key
   */
  cancel(key: string): void {
    this.pendingRequests.delete(key);
  }

  /**
   * Cancels all pending requests
   */
  cancelAll(): void {
    this.pendingRequests.clear();
  }

  /**
   * Gets the number of pending requests
   */
  getPendingCount(): number {
    return this.pendingRequests.size;
  }

  /**
   * Cleans up stale requests older than the specified timeout
   */
  cleanup(timeoutMs: number = this.defaultTimeout): void {
    const now = Date.now();
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.timestamp > timeoutMs) {
        this.pendingRequests.delete(key);
      }
    }
  }
}

// Global instance for organization service requests
export const orgRequestDeduplicator = new RequestDeduplicator();

/**
 * Creates a debounced function that delays execution until after the specified wait time
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): T & { cancel: () => void } {
  let timeoutId: NodeJS.Timeout | null = null;

  const debounced = ((...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  }) as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}

/**
 * Creates a throttled function that limits execution to once per specified interval
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): T & { cancel: () => void } {
  let inThrottle = false;
  let timeoutId: NodeJS.Timeout | null = null;

  const throttled = ((...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      timeoutId = setTimeout(() => {
        inThrottle = false;
      }, limit);
    }
  }) as T & { cancel: () => void };

  throttled.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    inThrottle = false;
  };

  return throttled;
}
